/**
 * Table view of the products list — the alternative to the card grid,
 * enabled via the ViewToggleAndCart toggle. Shows one row per product
 * (not grouped) with columns for code / brand / product / thickness /
 * grade / rate / Add button.
 *
 * When the current search returns zero products, an inline "Quick Add"
 * strip lets staff turn the search string into a new catalog entry via
 * the Laminate / Highlighter / Hardware / Adhesive Quick Add dialogs,
 * pre-populating the `product_code` field with whatever they typed.
 *
 * Extracted from ProductsPage.js. Pure JSX with the
 * parent's state + setters passed as props — 9 total. Chunky prop
 * signature is a deliberate trade-off vs. bringing context/reducer
 * plumbing into what is otherwise a leaf presentational component.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus, Palette, Sparkles, Wrench, Pipette } from 'lucide-react';
import { formatINR } from '../../../lib/store';


function EmptyStateQuickAddRow({
  search,
  onQuickAddLaminate,
  onQuickAddHighlighter,
  onQuickAddHardware,
  onQuickAddAdhesive,
}) {
  return (
    <tr>
      <td colSpan={7} className="text-center py-8">
        <div className="text-[#8F9485] mb-4">No products found for &quot;{search}&quot;</div>
        <div className="flex flex-wrap justify-center gap-2">
          <span className="text-xs text-[#5C6150]">Quick Add:</span>
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-[#1976D2] text-[#1976D2]"
            onClick={onQuickAddLaminate}
            data-testid="table-empty-quick-add-laminate">
            <Palette className="w-3 h-3 mr-1" /> Laminate
          </Button>
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-[#9C27B0] text-[#9C27B0]"
            onClick={onQuickAddHighlighter}
            data-testid="table-empty-quick-add-highlighter">
            <Sparkles className="w-3 h-3 mr-1" /> Highlighter
          </Button>
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-[#4A5568] text-[#4A5568]"
            onClick={onQuickAddHardware}
            data-testid="table-empty-quick-add-hardware">
            <Wrench className="w-3 h-3 mr-1" /> Hardware
          </Button>
          <Button size="sm" variant="outline"
            className="h-7 text-xs border-[#9B4DCA] text-[#9B4DCA]"
            onClick={onQuickAddAdhesive}
            data-testid="table-empty-quick-add-adhesive">
            <Pipette className="w-3 h-3 mr-1" /> Adhesive
          </Button>
        </div>
      </td>
    </tr>
  );
}


export function ProductsTableView({
  products,
  search,
  onAddProduct,
  onQuickAddLaminate,
  onQuickAddHighlighter,
  onQuickAddHardware,
  onQuickAddAdhesive,
}) {
  return (
    <div className="bg-white border border-[#E2E6D9] rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="bg-[#F2F4EF] border-b border-[#E2E6D9]">
              <th className="text-left py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Code</th>
              <th className="text-left py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Brand</th>
              <th className="text-left py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Product</th>
              <th className="text-left py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Thickness</th>
              <th className="text-left py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Grade</th>
              <th className="text-right py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Rate</th>
              <th className="text-center py-2 px-3 font-semibold text-[#5C6150] text-xs uppercase tracking-wide">Add</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <EmptyStateQuickAddRow
                search={search}
                onQuickAddLaminate={onQuickAddLaminate}
                onQuickAddHighlighter={onQuickAddHighlighter}
                onQuickAddHardware={onQuickAddHardware}
                onQuickAddAdhesive={onQuickAddAdhesive}
              />
            ) : (
              products.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-[#E2E6D9] hover:bg-[#F9FAF7] transition-colors"
                  data-testid={`product-row-${p.id}`}
                >
                  <td className="py-2 px-3 font-mono text-xs text-[#556B2F] font-medium">
                    {p.product_code || '-'}
                  </td>
                  <td className="py-2 px-3 font-semibold text-[#1A1C16]">{p.brand_name}</td>
                  <td className="py-2 px-3 text-[#1A1C16]">{p.subcategory || p.product_name}</td>
                  <td className="py-2 px-3 font-mono-data text-xs">{p.thickness}</td>
                  <td className="py-2 px-3 text-xs text-[#5C6150]">{p.grade}</td>
                  <td className="py-2 px-3 text-right font-mono-data font-semibold text-[#556B2F]">
                    {formatINR(p.base_rate)}
                    <span className="text-xs text-[#8F9485] ml-1">
                      /{p.rate_type?.replace('per ', '')}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center">
                    <Button
                      size="sm"
                      className="h-7 bg-[#556B2F] hover:bg-[#465926] text-white"
                      onClick={() => onAddProduct(p)}
                      data-testid={`add-product-btn-${p.id}`}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
